import React from 'react';

/**
 * Sliding segmented control (craft type, pricing mode).
 *
 * @param {object} props
 * @param {Array<{ value: string, label: React.ReactNode, icon?: React.ReactNode }>} props.options
 * @param {string} props.value
 * @param {(value: string) => void} props.onChange
 * @param {'fixed'|'equal'} [props.layout='fixed'] — equal = flex-1 buttons
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel]
 */
export default function SegmentedControl({
  options = [],
  value,
  onChange,
  layout = 'fixed',
  className = '',
  ariaLabel = 'Options',
}) {
  const count = options.length || 1;
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value)
  );
  const leftPercent = (activeIndex / count) * 100;
  const widthPercent = 100 / count;

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`relative inline-flex w-full bg-critic-bg-raised/80 backdrop-blur-xl rounded-2xl border border-white/[0.09] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${className}`.trim()}
    >
      <div
        className="absolute top-1.5 bottom-1.5 rounded-xl transition-all duration-500 ease-critic-out shadow-[0_8px_32px_rgba(0,122,255,0.35)] bg-gradient-insight pointer-events-none"
        style={{
          left: `calc(${leftPercent}% + 0.25rem)`,
          width: `calc(${widthPercent}% - 0.5rem)`,
        }}
        aria-hidden
      />
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange && onChange(opt.value)}
            className={`relative z-10 px-4 py-3.5 rounded-xl font-semibold text-sm sm:text-base transition-colors duration-critic focus-visible:ring-2 focus-visible:ring-apple-blue/80 focus-visible:ring-offset-2 focus-visible:ring-offset-critic-surface-deep ${
              layout === 'equal' ? 'flex-1' : ''
            } ${
              active
                ? 'text-white'
                : 'text-critic-text-muted hover:text-white hover:bg-white/[0.04]'
            }`}
          >
            <span className="inline-flex items-center justify-center gap-2">
              {opt.icon}
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
